import {
  useEffect,
  useId,
  useState,
  type ComponentType,
  type ReactNode,
} from "react";
import * as AlertDialogPrimitive from "@radix-ui/react-alert-dialog";
import { Button } from "../../Controls/Button";
import type { ButtonTone } from "../../Controls/Button";
import { PortalContextReset } from "../../Controls/Internal/PortalContextReset";
import { useDialogStackPosition } from "../DialogHostMutationContext";
import { DialogDepthProvider } from "./DialogDepthContext";
import { useDialogStackRegistration } from "./useDialogStackRegistration";
import {
  DIALOG_CONFIRM_OFFSET,
  DIALOG_CONTENT_BASE_Z,
  DIALOG_OVERLAY_BASE_Z,
  DIALOG_STACK_STRIDE,
} from "./dialogConstants";

interface ConfirmDialogIconColors {
  bg: string;
  border: string;
  fg: string;
}

interface ConfirmDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (input?: string) => void | Promise<void>;
  title: string;
  message: ReactNode;
  confirmLabel?: string | undefined;
  cancelLabel?: string | undefined;
  confirmTone?: ButtonTone | undefined;
  icon?: ComponentType<{ className?: string }> | undefined;
  iconColors?: ConfirmDialogIconColors | undefined;
  loading?: boolean | undefined;
  /** When set, shows a textarea for collecting user input. */
  inputLabel?: string | undefined;
  /** Placeholder text for the input textarea. */
  inputPlaceholder?: string | undefined;
  /** Pre-populates the textarea so edit flows can show the current value. */
  inputDefaultValue?: string | undefined;
}

export type { ConfirmDialogIconColors };

export function ConfirmDialog({
  open,
  onClose,
  onConfirm,
  title,
  message,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  confirmTone = "neutral",
  icon: IconProp,
  iconColors,
  loading = false,
  inputLabel,
  inputPlaceholder,
  inputDefaultValue,
}: ConfirmDialogProps) {
  const [inputValue, setInputValue] = useState(inputDefaultValue ?? "");
  const [asyncLoading, setAsyncLoading] = useState(false);
  const isLoading = loading || asyncLoading;

  // The component stays mounted across open/close cycles (Button's
  // StandardButton_ path renders us unconditionally and just toggles `open`).
  // Reset the textarea to `inputDefaultValue` each time the dialog re-opens
  // so a cancelled edit doesn't leak back on the next open.
  useEffect(() => {
    if (open) setInputValue(inputDefaultValue ?? "");
  }, [open, inputDefaultValue]);

  // Register in the same open-stack as Dialog so z-index scales with nesting.
  // Without this, ConfirmDialog used a hardcoded z=100 that broke once a
  // regular dialog reached stackPosition >= 3 (50 + 3*20 = 110 > 100).
  // CONFIRM_OFFSET keeps the confirm above the dialog at the same position.
  const stackId = useId();
  useDialogStackRegistration(stackId, open);
  const stackPosition = useDialogStackPosition(stackId);
  const overlayZ = DIALOG_OVERLAY_BASE_Z + stackPosition * DIALOG_STACK_STRIDE;
  const contentZ =
    DIALOG_CONTENT_BASE_Z +
    stackPosition * DIALOG_STACK_STRIDE +
    DIALOG_CONFIRM_OFFSET;

  // oxlint-disable-next-line fs/no-null -- React requires null for conditional rendering
  if (!open) return null;

  return (
    <AlertDialogPrimitive.Root open>
      <AlertDialogPrimitive.Portal>
        <AlertDialogPrimitive.Overlay
          className="fixed inset-0 backdrop-blur-[6px] backdrop-brightness-90"
          style={{ zIndex: overlayZ }}
        />
        {/* `dvh` not `vh`: Safari on iPad measures `vh` against the viewport
            with the toolbar hidden, which pushes the dialog lower than
            intended. The height cap plus the scrollable message region below
            stop a long message from carrying the buttons off the bottom —
            previously there was no cap at all, so they simply became
            unreachable. */}
        <AlertDialogPrimitive.Content
          className="fixed top-[25dvh] left-1/2 flex max-h-[70dvh] w-full max-w-md -translate-x-1/2 flex-col rounded-xl border-2 border-border bg-card p-6 shadow-2xl"
          style={{ zIndex: contentZ }}
        >
          {/* Full layout-context reset (not just ControlBar): a confirm
              opened from a table row / action bar / menu must not inherit
              those adaptations into its Cancel/Confirm buttons. */}
          <DialogDepthProvider value={stackPosition}>
            <PortalContextReset>
              <AlertDialogPrimitive.Title className="sr-only">
                {title}
              </AlertDialogPrimitive.Title>
              <AlertDialogPrimitive.Description className="sr-only">
                {title}
              </AlertDialogPrimitive.Description>
              <div className="flex shrink-0 items-center gap-3.5">
                {IconProp && iconColors && (
                  <div
                    className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full border-2 ${iconColors.bg} ${iconColors.border}`}
                  >
                    <IconProp className={`h-5 w-5 ${iconColors.fg}`} />
                  </div>
                )}
                <h3 className="text-xl font-bold text-foreground">{title}</h3>
              </div>
              <div className="mt-3 min-h-0 flex-auto overflow-y-auto">
                <div className="text-sm text-content-secondary">{message}</div>
                {inputLabel && (
                  <div className="mt-4">
                    <label className="mb-1.5 block text-xs font-bold text-content">
                      {inputLabel}
                    </label>
                    <textarea
                      value={inputValue}
                      onChange={(e) => setInputValue(e.target.value)}
                      placeholder={inputPlaceholder}
                      rows={3}
                      className="w-full rounded-lg border-2 border-border bg-card px-3 py-2 text-sm text-foreground transition-colors outline-none placeholder:text-content-faint focus:border-foreground/40"
                    />
                  </div>
                )}
              </div>
              <div className="mt-6 flex shrink-0 items-center justify-end gap-3">
                <Button
                  data-testid="confirm-dialog-cancel"
                  onClick={onClose}
                  disabled={isLoading}
                  disabledMessage="Loading..."
                >
                  {cancelLabel}
                </Button>
                <Button
                  data-testid="confirm-dialog-confirm"
                  primary
                  tone={confirmTone}
                  onClick={async () => {
                    setAsyncLoading(true);
                    try {
                      await onConfirm(inputLabel ? inputValue : undefined);
                      onClose();
                    } finally {
                      setAsyncLoading(false);
                    }
                  }}
                  loading={isLoading}
                >
                  {confirmLabel}
                </Button>
              </div>
            </PortalContextReset>
          </DialogDepthProvider>
        </AlertDialogPrimitive.Content>
      </AlertDialogPrimitive.Portal>
    </AlertDialogPrimitive.Root>
  );
}
